import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { formatChartValue } from '../../utils/chartFormatters';
import { transformForNetWorthChart, formatTooltipCurrency } from '../../utils/wealthTracking/chartDataTransformers';
import { useCountry } from '../../CountryContext';
import { getCurrencySymbol } from '../../countryConfig';

const NetWorthTrendChart = ({ snapshots, isDark }) => {
  const { selectedCountry } = useCountry();
  const currencySymbol = getCurrencySymbol(selectedCountry);

  // Transform data for chart
  const chartData = transformForNetWorthChart(snapshots);

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;

    const data = payload[0].payload;

    return (
      <div
        className={`p-3 rounded-lg shadow-lg border ${
          isDark
            ? 'bg-gray-800 border-gray-700 text-white'
            : 'bg-white border-gray-200 text-gray-900'
        }`}
      >
        <p className="font-semibold mb-2">{data.month}</p>
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center justify-between gap-4 text-sm">
            <span className="flex items-center gap-2">
              <span
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              {entry.name}:
            </span>
            <span className="font-semibold">
              {formatTooltipCurrency(entry.value, currencySymbol)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  if (!chartData || chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        <p>No data available. Create your first snapshot to see your wealth trend!</p>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={400}>
      <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
        <CartesianGrid
          strokeDasharray="3 3"
          stroke={isDark ? '#374151' : '#e5e7eb'}
        />
        <XAxis
          dataKey="month"
          stroke={isDark ? '#9ca3af' : '#6b7280'}
          tick={{ fill: isDark ? '#9ca3af' : '#6b7280' }}
        />
        <YAxis
          stroke={isDark ? '#9ca3af' : '#6b7280'}
          tick={{ fill: isDark ? '#9ca3af' : '#6b7280' }}
          tickFormatter={(value) => formatChartValue(value, currencySymbol)}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend
          wrapperStyle={{ color: isDark ? '#9ca3af' : '#6b7280' }}
        />
        <Line
          type="monotone"
          dataKey="netWorth"
          stroke="#3b82f6"
          strokeWidth={3}
          name="Net Worth"
          dot={{ fill: '#3b82f6', r: 4 }}
          activeDot={{ r: 6 }}
        />
        <Line
          type="monotone"
          dataKey="totalAssets"
          stroke="#10b981"
          strokeWidth={2}
          name="Total Assets"
          dot={{ fill: '#10b981', r: 3 }}
        />
        <Line
          type="monotone"
          dataKey="totalLiabilities"
          stroke="#ef4444"
          strokeWidth={2}
          name="Total Liabilities"
          dot={{ fill: '#ef4444', r: 3 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

export default NetWorthTrendChart;
